import React, { useState } from "react";
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { useDropzone } from "react-dropzone";
import { nanoid } from "nanoid";
import { Trash2, Plus } from "lucide-react";

import { storage } from "../../firebase.js";
import Message from "../../components/Message.js";

const MAX_BYTES = 10 * 1024 * 1024;

// Haalt het opslagpad uit een Firebase download-URL, zodat een foto die bij een
// hervatte onboarding is ingeladen ook weer te verwijderen is. Geeft null als
// het pad niet te bepalen is (dan slaan we het verwijderen uit storage over).
function pathFromUrl(url) {
  try {
    const match = decodeURIComponent(url).match(/\/o\/([^?]+)/);
    return match ? match[1] : null;
  } catch (_e) {
    return null;
  }
}

// Foto-upload voor de dienst-onboarding. Zelfde opzet als de feestlocatie-
// galerij (react-dropzone + Firebase Storage), maar bewaart de download-URL's
// zodat de publieke site ze direct kan tonen. `photos` is een array van
// { url, path }; `onChange` krijgt de nieuwe array.
// `onChange` moet een React state-setter zijn: we gebruiken de functionele
// vorm zodat opeenvolgende/parallelle uploads elkaar niet overschrijven.
function PhotoUpload({ serviceId, photos, onChange }) {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    disabled: uploading,
    accept: { "image/*": [] },
    onDrop: (accepted) => {
      setError("");
      const file = accepted[0];
      if (!file) return;
      if (file.size > MAX_BYTES) {
        setError("Deze afbeelding is te groot (max. 10 MB).");
        return;
      }
      const nid = nanoid(10).toLowerCase();
      const ext = (file.type?.split("/")[1] || file.name.split(".").pop() || "jpg").toLowerCase();
      const path = `services/${serviceId}/images/${nid}.${ext}`;
      setUploading(true);
      setProgress(0);
      const task = uploadBytesResumable(ref(storage, path), file);
      task.on(
        "state_changed",
        (snap) => setProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        (err) => {
          console.log(err);
          setError("Uploaden is mislukt. Probeer het opnieuw.");
          setUploading(false);
          setProgress(0);
        },
        async () => {
          try {
            const url = await getDownloadURL(task.snapshot.ref);
            onChange((prev) => [...prev, { url, path }]);
          } catch (err) {
            console.log(err);
            setError("Uploaden is mislukt. Probeer het opnieuw.");
          }
          setUploading(false);
          setProgress(0);
        },
      );
    },
  });

  async function remove(photo) {
    onChange((prev) => prev.filter((p) => p.url !== photo.url));
    const path = photo.path || pathFromUrl(photo.url);
    if (path) {
      try {
        await deleteObject(ref(storage, path));
      } catch (_e) {
        // Bestand al weg of geen rechten: negeren, uit de lijst is genoeg.
      }
    }
  }

  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {photos.map((photo) => (
          <div key={photo.url} className="relative group">
            <img src={photo.url} alt="" className="w-full aspect-square object-cover rounded-sm" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-fast rounded-sm">
              <button
                type="button"
                onClick={() => remove(photo)}
                className="absolute top-1.5 right-1.5 p-1.5 bg-error text-white rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-fast hover:bg-red-700"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}

        <div
          {...getRootProps()}
          className="flex flex-col items-center justify-center aspect-square border-2 border-dashed border-border hover:border-accent hover:bg-accent-subtle rounded-sm cursor-pointer transition-all duration-fast"
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-text-secondary font-medium">{progress}%</span>
            </div>
          ) : (
            <>
              <Plus size={24} strokeWidth={1.5} className="text-text-tertiary mb-1" />
              <span className="text-xs text-text-tertiary">Foto toevoegen</span>
            </>
          )}
        </div>
      </div>
      {error && (
        <div className="mt-3">
          <Message text={error} />
        </div>
      )}
    </div>
  );
}

export default PhotoUpload;
